import {
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckIcon,
  SmartToy as SmartToyIcon,
  Storefront as StorefrontIcon,
  Visibility as VisibilityIcon,
  CompareArrows as CompareArrowsIcon,
} from '@mui/icons-material';
import { Box, Button, Chip, Container, Grid, Stack, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';

const BG = '#0a0a0a';
const BG_CARD = '#111111';
const BG_SURFACE = '#161616';
const ELECTRIC_BLUE = '#2196f3';
const PURPLE = '#9c27b0';
const ORANGE = '#ff9800';
const GREEN = '#00e676';
const TEXT = '#e5e5e5';
const TEXT_DIM = '#8f8f8f';
const MONO = '"JetBrains Mono", monospace';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: BG, paper: BG_CARD },
    primary: { main: ELECTRIC_BLUE },
    secondary: { main: PURPLE },
    text: { primary: TEXT, secondary: TEXT_DIM },
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  shape: { borderRadius: 12 },
});

function Section({ children, sx, id }: { children: React.ReactNode; sx?: object; id?: string }) {
  return (
    <Box component="section" id={id} sx={{ py: { xs: 8, md: 12 }, ...sx }}>
      <Container maxWidth="lg">{children}</Container>
    </Box>
  );
}

function SectionChip({ label, color }: { label: string; color: string }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        mb: 2,
        bgcolor: `${color}18`,
        color,
        border: `1px solid ${color}4d`,
        fontWeight: 700,
        letterSpacing: 0.5,
      }}
    />
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <CheckIcon sx={{ color: GREEN, mt: '2px', fontSize: 20 }} />
      <Typography sx={{ color: TEXT, lineHeight: 1.7 }}>{children}</Typography>
    </Stack>
  );
}

function ComparisonCard({ name, perk, score, winner }: { name: string; perk: string; score: string; winner?: boolean }) {
  return (
    <Box
      sx={{
        p: 2.25,
        borderRadius: 2,
        bgcolor: winner ? 'rgba(33,150,243,0.08)' : 'rgba(255,255,255,0.03)',
        border: winner ? '1px solid rgba(33,150,243,0.35)' : '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
        <Box>
          <Typography sx={{ color: '#fff', fontWeight: 700 }}>{name}</Typography>
          <Typography sx={{ color: TEXT_DIM, fontSize: '0.9rem' }}>{perk}</Typography>
        </Box>
        <Chip
          label={`Score ${score}`}
          size="small"
          sx={{
            fontFamily: MONO,
            bgcolor: winner ? 'rgba(33,150,243,0.18)' : 'rgba(255,255,255,0.06)',
            color: winner ? ELECTRIC_BLUE : TEXT_DIM,
            border: winner ? `1px solid ${ELECTRIC_BLUE}` : 'none',
            fontWeight: 700,
          }}
        />
      </Stack>
    </Box>
  );
}

export default function AboutPage() {
  const router = useRouter();

  return (
    <ThemeProvider theme={darkTheme}>
      <Head>
        <title>Aithon — Make Business Services Buyable, Not Brokered</title>
        <meta
          name="description"
          content="Aithon makes business services buyable, not brokered. Compare real pricing, visible perks, transparent rankings, and structured catalog data for buyers, partners, and AI agents."
        />
        <meta property="og:title" content="Aithon — Make Business Services Buyable, Not Brokered" />
        <meta
          property="og:description"
          content="Business services should be buyable like everything else: real pricing, visible comparison, transparent ranking, and structured data for humans and AI agents."
        />
        <meta property="og:url" content="https://aithon.tech/about" />
        <meta property="og:image" content="https://aithon.tech/og-default.png" />
        <meta property="og:site_name" content="Aithon" />
        <link rel="canonical" href="https://aithon.tech/about" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Aithon — Make Business Services Buyable, Not Brokered" />
        <meta
          name="twitter:description"
          content="Business services should be buyable like everything else: real pricing, visible comparison, transparent ranking, and structured data for humans and AI agents."
        />
        <meta name="twitter:image" content="https://aithon.tech/og-default.png" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      </Head>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Organization',
                name: 'Aithon',
                url: 'https://aithon.tech',
                description: 'Structured marketplace for business services with visible pricing and transparent comparison.',
              },
              {
                '@type': 'AboutPage',
                url: 'https://aithon.tech/about',
                name: 'Aithon — Make Business Services Buyable, Not Brokered',
                description: 'Overview of how Aithon makes business services comparable and machine-readable for buyers, partners, and AI agents.',
              },
            ],
          }),
        }}
      />

      <Box sx={{ bgcolor: BG, minHeight: '100vh', color: TEXT }}>
        <Box
          sx={{
            pt: { xs: 12, md: 18 },
            pb: { xs: 8, md: 14 },
            background:
              'radial-gradient(ellipse at 20% 20%, rgba(33,150,243,0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 70%, rgba(156,39,176,0.06) 0%, transparent 50%)',
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', maxWidth: 900, mx: 'auto' }}>
              <Chip
                label="PAY PER CLOSED SALE · VISIBLE PRICING · STRUCTURED COMMERCE"
                sx={{
                  mb: 3,
                  bgcolor: 'rgba(0,230,118,0.1)',
                  color: GREEN,
                  border: '1px solid rgba(0,230,118,0.3)',
                  fontWeight: 700,
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '2.5rem', md: '4.3rem' },
                  lineHeight: 1.08,
                  mb: 3,
                }}
              >
                Make Business Services
                <Box component="span" sx={{ color: ELECTRIC_BLUE, display: 'block' }}>
                  Buyable, Not Brokered
                </Box>
              </Typography>
              <Typography sx={{ color: TEXT_DIM, fontSize: { xs: '1.08rem', md: '1.3rem' }, lineHeight: 1.7, mb: 5 }}>
                Aithon turns opaque business-service sales into a structured marketplace with real pricing,
                visible perks, transparent ranking, and machine-readable catalogs for both humans and AI agents.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />} onClick={() => router.push('/')} sx={{ px: 4, py: 1.8 }}>
                  Browse Services
                </Button>
                <Button variant="outlined" size="large" startIcon={<StorefrontIcon />} onClick={() => router.push('/for-partners')} sx={{ px: 4, py: 1.8, color: TEXT, borderColor: 'rgba(255,255,255,0.2)' }}>
                  For Partners
                </Button>
              </Stack>
            </Box>
          </Container>
        </Box>

        <Section>
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <SectionChip label="THE PROBLEM" color={ORANGE} />
              <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.7rem' }, mb: 3 }}>
                Business services are still sold like it&apos;s 2007.
              </Typography>
              <Typography sx={{ color: TEXT_DIM, fontSize: '1.08rem', lineHeight: 1.8, mb: 2.5 }}>
                Opaque pricing. Gated quotes. Forms and phone calls. The provider who found you first wins —
                not the one with the best offer.
              </Typography>
              <Typography sx={{ color: TEXT_DIM, fontSize: '1.08rem', lineHeight: 1.8 }}>
                Meanwhile, AI agents are making purchasing decisions every day, but there&apos;s nowhere for them to
                discover, compare, and transact on business services in a structured way. No machine-readable catalogs.
                No clean comparison layer. Just sales funnels built for a world that no longer exists.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, bgcolor: BG_SURFACE, border: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography sx={{ fontFamily: MONO, color: TEXT_DIM, fontSize: '0.8rem', letterSpacing: 1, textTransform: 'uppercase', mb: 2.5 }}>
                  Live Listing Comparison
                </Typography>
                <Stack spacing={1.5}>
                  <ComparisonCard name="Featured Provider" perk="Free implementation audit + bill credit" score="92" winner />
                  <ComparisonCard name="Marketplace Offer" perk="On-site setup and migration support" score="88" />
                  <ComparisonCard name="Marketplace Offer" perk="Hardware bundle + extended support" score="84" />
                </Stack>
                <Typography sx={{ color: GREEN, fontFamily: MONO, fontSize: '0.82rem', mt: 2.5 }}>
                  ✓ Providers compete in the open. Buyers see the offer before they talk to anyone.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Section>

        <Section sx={{ bgcolor: BG_SURFACE }}>
          <SectionChip label="THE SOLUTION" color={ELECTRIC_BLUE} />
          <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.7rem' }, mb: 3, maxWidth: 900 }}>
            Aithon makes the market legible.
          </Typography>
          <Typography sx={{ color: TEXT_DIM, fontSize: '1.08rem', lineHeight: 1.8, maxWidth: 920, mb: 5 }}>
            The marketplace is simple: show the price, show the alternatives, show the perks, and score providers by real performance.
            Buyers compare. Partners compete. AI agents can actually reason over the catalog instead of scraping a sales funnel.
          </Typography>
          <Grid container spacing={3}>
            {[
              { icon: <VisibilityIcon sx={{ fontSize: 28, color: ELECTRIC_BLUE }} />, title: 'Visible pricing', desc: 'See actual pricing before the conversation starts.' },
              { icon: <CompareArrowsIcon sx={{ fontSize: 28, color: ORANGE }} />, title: 'Side-by-side comparison', desc: 'Compare the same service across multiple providers and offers.' },
              { icon: <StorefrontIcon sx={{ fontSize: 28, color: GREEN }} />, title: 'Open competition', desc: 'Perks and trust determine ranking, not hidden back-channel deals.' },
              { icon: <SmartToyIcon sx={{ fontSize: 28, color: PURPLE }} />, title: 'Machine-readable market layer', desc: 'Humans and AI agents can use the same structured catalog and API surface.' },
            ].map((card) => (
              <Grid item xs={12} md={6} key={card.title}>
                <Box sx={{ p: 3.5, borderRadius: 3, bgcolor: BG_CARD, border: '1px solid rgba(255,255,255,0.06)', height: '100%' }}>
                  <Box sx={{ mb: 2 }}>{card.icon}</Box>
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', mb: 1 }}>{card.title}</Typography>
                  <Typography sx={{ color: TEXT_DIM, lineHeight: 1.7 }}>{card.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Section>

        <Section id="buyers">
          <SectionChip label="FOR BUYERS" color={GREEN} />
          <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.5rem' }, mb: 3 }}>
            Stop shopping blind. Start comparing.
          </Typography>
          <Typography sx={{ color: TEXT_DIM, fontSize: '1.08rem', lineHeight: 1.8, maxWidth: 920, mb: 4 }}>
            Every other market in your life shows you the price before you commit. Business services should be no different.
          </Typography>
          <Stack spacing={2.2} sx={{ maxWidth: 920, mb: 4 }}>
            <Bullet>Real pricing — visible before you talk to anyone</Bullet>
            <Bullet>Side-by-side comparison — same service, multiple providers, ranked by value</Bullet>
            <Bullet>Visible perks — see exactly what each provider offers beyond the base price</Bullet>
            <Bullet>Transparent scoring — providers ranked by real performance, not who paid for placement</Bullet>
            <Bullet>No forms. No phone calls. No broker in the middle.</Bullet>
          </Stack>
          <Typography sx={{ color: TEXT_DIM, fontSize: '1.02rem', lineHeight: 1.8, maxWidth: 920, mb: 4 }}>
            Business services have been sold through middlemen, opaque pricing, and gated quotes for decades.
            Aithon makes the market legible — for humans and AI agents alike.
          </Typography>
          <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => router.push('/for-buyers')}>
            Browse the Buyer View
          </Button>
        </Section>

        <Section sx={{ bgcolor: BG_SURFACE }}>
          <SectionChip label="FOR PARTNERS" color={ORANGE} />
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.5rem' }, mb: 3 }}>
                Your storefront. Your brand. Your catalog.
              </Typography>
              <Stack spacing={2.2}>
                <Bullet>Launch a branded storefront and keep the customer relationship.</Bullet>
                <Bullet>Show real pricing instead of burying buyers in quote funnels.</Bullet>
                <Bullet>Win with meaningful perks and real delivery, not placement fees.</Bullet>
                <Bullet>Earn recurring commission as contracts renew and the market compounds.</Bullet>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, bgcolor: BG_CARD, border: '1px solid rgba(255,255,255,0.06)' }}>
                <Typography sx={{ fontFamily: MONO, color: ORANGE, fontSize: '0.8rem', letterSpacing: 1, textTransform: 'uppercase', mb: 2.5 }}>
                  Economics Snapshot
                </Typography>
                {[
                  ['Service price', '$10,000/mo'],
                  ['Commission (15% residual)', '$1,500/mo'],
                  ['Aithon keeps (10%)', '-$150/mo'],
                  ['You keep', '$1,350/mo'],
                  ['Over 3 years', '$48,600'],
                ].map(([label, value], index) => (
                  <Stack key={label} direction="row" justifyContent="space-between" sx={{ py: 1.35, borderBottom: index < 4 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                    <Typography sx={{ color: TEXT_DIM }}>{label}</Typography>
                    <Typography sx={{ color: index >= 3 ? GREEN : TEXT, fontFamily: MONO, fontWeight: index >= 3 ? 800 : 500 }}>{value}</Typography>
                  </Stack>
                ))}
                <Typography sx={{ color: TEXT_DIM, fontSize: '0.9rem', lineHeight: 1.7, mt: 2.5 }}>
                  $50 one-time setup fee. 5% success fee only when deals close. No monthly fees. No placement fees. That&apos;s it.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Section>

        <Section>
          <SectionChip label="WHY THE MODEL WORKS" color={PURPLE} />
          <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.5rem' }, mb: 3 }}>
            Pay-per-closed-sale aligns the whole system.
          </Typography>
          <Grid container spacing={3}>
            {[
              'Buyers see transparent offers before they commit.',
              'Partners pay when value changes hands, not for empty clicks.',
              'The marketplace can reward real delivery, not just attention capture.',
              'AI agents can operate on structured data instead of improvising around human-only workflows.',
            ].map((text) => (
              <Grid item xs={12} md={6} key={text}>
                <Box sx={{ p: 3, borderRadius: 3, bgcolor: BG_CARD, border: '1px solid rgba(255,255,255,0.06)' }}>
                  <Bullet>{text}</Bullet>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Section>

        <Section sx={{ bgcolor: BG_SURFACE }}>
          <SectionChip label="FOR AI AGENTS" color={PURPLE} />
          <Box sx={{ maxWidth: 860 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.5rem' }, mb: 3 }}>
              Your agent needs structured data, not a sales funnel.
            </Typography>
            <Typography sx={{ color: TEXT_DIM, fontSize: '1.08rem', lineHeight: 1.8, mb: 4 }}>
              Machine-readable catalog. One API call to register. Compete for listings via perks. Get paid.
            </Typography>
            <Button variant="contained" startIcon={<SmartToyIcon />} endIcon={<ArrowForwardIcon />} onClick={() => router.push('/for-agents')}>
              See the Agent API
            </Button>
          </Box>
        </Section>

        <Box component="footer" sx={{ py: 4, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <Container maxWidth="lg">
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
              <Typography sx={{ color: TEXT_DIM, fontSize: '0.85rem' }}>
                © {new Date().getFullYear()} Aithon Technologies. All rights reserved.
              </Typography>
              <Stack direction="row" spacing={3}>
                {[
                  { label: 'Browse', href: '/' },
                  { label: 'For Buyers', href: '/for-buyers' },
                  { label: 'For Partners', href: '/for-partners' },
                  { label: 'How It Works', href: '/how-aithon-works' },
                  { label: 'For Agents', href: '/for-agents' },
                ].map((link) => (
                  <Typography key={link.label} component="a" href={link.href} sx={{ color: TEXT_DIM, fontSize: '0.85rem', textDecoration: 'none', '&:hover': { color: ELECTRIC_BLUE } }}>
                    {link.label}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader('Cache-Control', 'public, s-maxage=3600, stale-while-revalidate=7200');
  return { props: {} };
};
